import { useState, useEffect, useMemo } from 'react';
import { uploadImage } from '../../lib/cloudinaryApi';
import { isYieldTrackingProduct } from '../../lib/inventoryYield';
import {
  PROMOTION_WEEKDAYS,
  calculatePromotionComponentsCost,
  getPromotionComponents,
  isPromotionCategory,
  isPromotionProduct,
} from '../../lib/promotionRules';

// --- Helpers de imagen (sin dependencias) ---
const MAX_DIM = 1280;            // ↔️ mayor dimensión
const OUTPUT_MIME = 'image/webp'; // WebP = mejor peso/calidad
const OUTPUT_QUALITY = 0.82;      // 0–1

async function compressImageFile(file, { maxDim = MAX_DIM } = {}) {
  const img = new Image();
  const objectUrl = URL.createObjectURL(file);
  img.src = objectUrl;

  // decode con fallback
  try {
    await img.decode();
  } catch {
    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
    });
  }



  // redimensionado proporcional
  let { width, height } = img;
  const ratio = Math.min(maxDim / width, maxDim / height, 1);
  const targetW = Math.round(width * ratio);
  const targetH = Math.round(height * ratio);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, targetW, targetH);

  // intento WebP, si no, JPEG
  let blob = await new Promise((res) =>
    canvas.toBlob(res, OUTPUT_MIME, OUTPUT_QUALITY)
  );
  if (!blob) {
    blob = await new Promise((res) =>
      canvas.toBlob(res, 'image/jpeg', 0.8)
    );
  }

  URL.revokeObjectURL(objectUrl);
  return blob;
}


const ProductForm = ({ onSave, onCancel, currentUser, categories: existingCategories, initialProductData, products = [], mode = 'create' }) => {
    const [loading, setLoading] = useState(false);
const [product, setProduct] = useState({
  name: '',
  barcode: '',
  image: '',
  price: '',
  costPrice: '',
  category: '',
  type: 'final',   // 👈 nuevo
  unit: '',        // 👈 nuevo (solo aplica si es insumo)
registeredBy: typeof currentUser === 'string'
  ? currentUser
  : (currentUser?.email
      ?? currentUser?.username
      ?? currentUser?.displayName
      ?? currentUser?.uid
      ?? ''),
  registrationDate: new Date().toISOString().split('T')[0]
});
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

const [iceConfig, setIceConfig] = useState({
    isIceCream: false,
  base: 'cono',          // 'cono' | 'vaso'
  sabores: 1,            // 1, 2, 3...
  gramosPorSabor: 90,    // ej. 90 g por sabor
  servilletas: 2,
cucharas: 1,
  baseInsumoId: '',     // ← nuevo
  baseInsumoName: ''    // ← nuevo (solo por comodidad visual)});
   });
const [submitting, setSubmitting] = useState(false);
// ===== Receta genérica =====
const [isRecipe, setIsRecipe] = useState(!!initialProductData?.config?.recipe);
const [baseItems, setBaseItems] = useState(initialProductData?.config?.recipe?.baseItems || []);
const [choiceGroups, setChoiceGroups] = useState(initialProductData?.config?.recipe?.choiceGroups || []);
const [promotionConfig, setPromotionConfig] = useState({
  enabled: !!initialProductData?.config?.promotion?.enabled,
  components: getPromotionComponents(initialProductData).length > 0
    ? getPromotionComponents(initialProductData)
    : [{ sourceProductId: '', sourceProductName: '', quantity: 2 }],
  weekdays: Array.isArray(initialProductData?.config?.promotion?.weekdays)
    ? initialProductData.config.promotion.weekdays.map(Number)
    : [],
});
const [selectedBaseProduct, setSelectedBaseProduct] = useState(null);
const [iceExtraItems, setIceExtraItems] = useState(initialProductData?.config?.extraItems || []);
const [selectedIceExtraProduct, setSelectedIceExtraProduct] = useState(null);

// Autocompletar de insumos
const [q, setQ] = useState('');
const [suggestions, setSuggestions] = useState([]);
const [newBaseQty, setNewBaseQty] = useState(1);
const [newBaseUnit, setNewBaseUnit] = useState('unidad');
const [iceExtraQ, setIceExtraQ] = useState('');
const [iceExtraSuggestions, setIceExtraSuggestions] = useState([]);
const [newIceExtraQty, setNewIceExtraQty] = useState(1);
const [previewUrl, setPreviewUrl] = useState(null);
const [uploadPct, setUploadPct]   = useState(0);

const normalizedType = (p) => (p?.type || 'final');
const yieldTrackingEnabled = isYieldTrackingProduct(product);
const normalizeRecipeUnit = (unit) => {
  const value = String(unit || '').trim().toLowerCase();
  if (value === 'gramo' || value === 'gramos' || value === 'gr') return 'g';
  if (value === 'mililitro' || value === 'mililitros') return 'ml';
  return value || 'unidad';
};
const formatThousandsInput = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('es-CO');
};
const sanitizeDecimalInput = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const normalized = raw.replace(/\./g, '').replace(',', '.');
  const hasTrailingSeparator = /[.,]$/.test(raw);
  const [integerPartRaw, ...decimalParts] = normalized.split('.');
  const integerPart = integerPartRaw.replace(/\D/g, '');
  const decimalPart = decimalParts.join('').replace(/\D/g, '').slice(0, 4);

  if (!integerPart && !decimalPart) return '';
  if (!decimalPart) {
    if (hasTrailingSeparator) return `${integerPart || '0'}.`;
    return integerPart;
  }
  return `${integerPart || '0'}.${decimalPart}`;
};
const formatDecimalThousandsInput = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const normalized = typeof value === 'number'
    ? raw
    : (/^\d+(\.\d+)?$/.test(raw) ? raw : sanitizeDecimalInput(raw));
  if (!normalized) return '';

  const [integerPart, decimalPart = ''] = normalized.split('.');
  const groupedInteger = Number(integerPart || 0).toLocaleString('es-CO');
  if (normalized.endsWith('.') && !decimalPart) return `${groupedInteger},`;
  return decimalPart ? `${groupedInteger},${decimalPart}` : groupedInteger;
};
const getProductBaseUnit = (prod) => normalizeRecipeUnit(prod?.unit);
const formatUnitLabel = (unit) => {
  const normalized = normalizeRecipeUnit(unit);
  if (normalized === 'g') return 'gramos';
  if (normalized === 'ml') return 'ml';
  return 'unidad';
};
const norm = (s='') =>
  s.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const isIceCreamFlavorCategory = (category) => {
  const normalized = norm(category || '');
  return normalized === 'sabor de helado' || normalized === 'sabores de helado';
};

const productUnitById = useMemo(() => {
  const map = new Map();
  (products || []).forEach((prod) => {
    if (prod?.id) map.set(prod.id, getProductBaseUnit(prod));
  });
  return map;
}, [products]);
const productById = useMemo(() => {
  const map = new Map();
  (products || []).forEach((prod) => {
    if (prod?.id) map.set(prod.id, prod);
  });
  return map;
}, [products]);
const effectiveCategory = showNewCategoryInput ? newCategory : product.category;
const promotionMode = (product.type || 'final') !== 'insumo' && isPromotionCategory(effectiveCategory);
const promotionComponents = Array.isArray(promotionConfig.components) ? promotionConfig.components : [];
const promotionCost = calculatePromotionComponentsCost(promotionComponents, products);
const promotionIncludedUnits = promotionComponents.reduce(
  (total, component) => total + (Number.parseInt(component.quantity, 10) || 0),
  0
);
const promotionSourceProducts = useMemo(() => (
  (products || [])
    .filter((prod) => prod?.id && prod.id !== initialProductData?.id)
    .filter((prod) => prod.deleted !== true)
    .filter((prod) => !isPromotionProduct(prod))
    .filter((prod) => !!prod?.config?.recipe)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' }))
), [products, initialProductData?.id]);
const insumoCategories = useMemo(() => (
  Array.from(new Set(
    (products || [])
      .filter((prod) => normalizedType(prod) === 'insumo')
      .map((prod) => String(prod.category || '').trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
), [products]);
const costByProductId = useMemo(() => {
  const map = new Map();
  (products || []).forEach((prod) => {
    if (prod?.id) map.set(prod.id, Number(prod.costPrice || 0));
  });
  return map;
}, [products]);
const recipeCostSummary = useMemo(() => {
  if (!isRecipe) {
    return { baseCost: 0, choiceCost: 0, total: 0, missingBaseItems: [], missingChoiceGroups: [] };
  }

  const baseCost = baseItems.reduce((sum, item) => {
    const unitCost = costByProductId.get(item.productId);
    if (unitCost == null) return sum;
    return sum + (Number(item.qty || 0) * Number(unitCost || 0));
  }, 0);

  const missingBaseItems = baseItems
    .filter((item) => !costByProductId.has(item.productId))
    .map((item) => item.name);

  const choiceCost = choiceGroups.reduce((sum, group) => {
    const wantedCategory = norm(group.fromCategory || '');
    const wantedUnit = normalizeRecipeUnit(group.unit || 'unidad');
    const matches = (products || []).filter((prod) =>
      normalizedType(prod) === 'insumo' &&
      norm(prod.category || '') === wantedCategory &&
      normalizeRecipeUnit(prod.unit) === wantedUnit
    );
    if (matches.length === 0) return sum;
    const defaultProduct = group.defaultProductId
      ? matches.find((prod) => prod.id === group.defaultProductId)
      : null;
    const estimatedUnitCost = defaultProduct
      ? Number(defaultProduct.costPrice || 0)
      : (() => {
        const pricedMatches = matches.filter((prod) => Number(prod.costPrice || 0) > 0);
        const costSource = pricedMatches.length > 0 ? pricedMatches : matches;
        return costSource.reduce((acc, prod) => acc + Number(prod.costPrice || 0), 0) / costSource.length;
      })();
    const picks = Math.max(Number(group.max || group.min || 0), 0);
    return sum + (estimatedUnitCost * Number(group.perPickQty || 0) * picks);
  }, 0);

  const missingChoiceGroups = choiceGroups
    .filter((group) => {
      const wantedCategory = norm(group.fromCategory || '');
      const wantedUnit = normalizeRecipeUnit(group.unit || 'unidad');
      const matches = (products || []).filter((prod) =>
        normalizedType(prod) === 'insumo' &&
        norm(prod.category || '') === wantedCategory &&
        normalizeRecipeUnit(prod.unit) === wantedUnit
      );
      if (matches.length === 0) return true;
      return !!group.defaultProductId && !matches.some((prod) => prod.id === group.defaultProductId);
    })
    .map((group) => group.label || 'Opción');

  const total = baseCost + choiceCost;
  return { baseCost, choiceCost, total, missingBaseItems, missingChoiceGroups };
}, [isRecipe, baseItems, choiceGroups, products, costByProductId]);
const iceCreamCostSummary = useMemo(() => {
  if (!iceConfig.isIceCream) {
    return {
      baseCost: 0,
      toolsCost: 0,
      extrasCost: 0,
      flavorCost: 0,
      total: 0,
      missingItems: [],
      missingFlavorCosts: false,
    };
  }

  const missingItems = [];

  const baseCost = (() => {
    if (!iceConfig.baseInsumoId) {
      missingItems.push('base');
      return 0;
    }
    if (!costByProductId.has(iceConfig.baseInsumoId)) {
      missingItems.push(iceConfig.baseInsumoName || 'base');
      return 0;
    }
    return Number(costByProductId.get(iceConfig.baseInsumoId) || 0);
  })();

  const servCount = Number(iceConfig.servilletas || 0);
  const cuchCount = Number(iceConfig.cucharas || 0);
  const servInsumo = products.find((prod) =>
    normalizedType(prod) === 'insumo' &&
    normalizeRecipeUnit(prod.unit) === 'unidad' &&
    norm(prod.name).includes('servillet')
  );
  const cuchInsumo = products.find((prod) =>
    normalizedType(prod) === 'insumo' &&
    normalizeRecipeUnit(prod.unit) === 'unidad' &&
    norm(prod.name).includes('cuchar')
  );

  let toolsCost = 0;
  if (servCount > 0) {
    if (!servInsumo || !costByProductId.has(servInsumo.id)) missingItems.push('servilletas');
    else toolsCost += Number(costByProductId.get(servInsumo.id) || 0) * servCount;
  }
  if (cuchCount > 0) {
    if (!cuchInsumo || !costByProductId.has(cuchInsumo.id)) missingItems.push('cucharas');
    else toolsCost += Number(costByProductId.get(cuchInsumo.id) || 0) * cuchCount;
  }

  const extrasCost = iceExtraItems.reduce((sum, item) => {
    if (!costByProductId.has(item.productId)) {
      missingItems.push(item.name || 'extra');
      return sum;
    }
    return sum + (Number(item.qty || 0) * Number(costByProductId.get(item.productId) || 0));
  }, 0);

  const flavorMatches = (products || []).filter((prod) =>
    normalizedType(prod) === 'insumo' &&
    normalizeRecipeUnit(prod.unit) === 'g' &&
    (norm(prod.name).startsWith('helado') || norm(prod.category).includes('helado'))
  );
  const avgFlavorCost = flavorMatches.length > 0
    ? flavorMatches.reduce((acc, prod) => acc + Number(prod.costPrice || 0), 0) / flavorMatches.length
    : 0;
  const totalFlavorGrams = Number(iceConfig.sabores || 0) * Number(iceConfig.gramosPorSabor || 0);
  const flavorCost = avgFlavorCost * totalFlavorGrams;
  const missingFlavorCosts = totalFlavorGrams > 0 && flavorMatches.length === 0;

  const total = baseCost + toolsCost + extrasCost + flavorCost;
  return {
    baseCost,
    toolsCost,
    extrasCost,
    flavorCost,
    total,
    missingItems: Array.from(new Set(missingItems)),
    missingFlavorCosts,
  };
}, [iceConfig, iceExtraItems, products, costByProductId]);
const baseItemsWithUnitMismatch = useMemo(() => (
  baseItems.filter((item) => {
    const expectedUnit = productUnitById.get(item.productId);
    if (!expectedUnit) return false;
    return normalizeRecipeUnit(item.unit) !== expectedUnit;
  })
), [baseItems, productUnitById]);

// --- Helpers para insumo base (cono/vaso) ---
const baseCandidates = (productsList, base) => {
  const want = norm(base || 'vaso'); // 'vaso' | 'cono'
  return (productsList || [])
    .filter(p => (p?.type || 'final') === 'insumo' && String(p.unit).toLowerCase() === 'unidad')
    .filter(p => norm(p.name).includes(want))
    .sort((a,b) => norm(a.name).localeCompare(norm(b.name)));
};


useEffect(() => {
  if (initialProductData) {
    const categoriaFormateada = typeof initialProductData.category === 'string'
      ? initialProductData.category
      : initialProductData.category?.value || '';

setProduct({
  ...initialProductData,
  category: categoriaFormateada,
   excludeFromEntries: !!initialProductData.excludeFromEntries,
  registeredBy: (typeof initialProductData.registeredBy === 'string')
    ? initialProductData.registeredBy
    : (initialProductData.registeredBy?.email
        ?? initialProductData.registeredBy?.username
        ?? initialProductData.registeredBy?.displayName
        ?? initialProductData.registeredBy?.uid
        ?? '')
});
// Precargar config si el producto ya la trae
if (initialProductData?.config) {
  setIceConfig({
    isIceCream: !!initialProductData.config.isIceCream,
    base: initialProductData.config.base || 'cono',
    sabores: Number(initialProductData.config.sabores || 1),
    gramosPorSabor: Number(initialProductData.config.gramosPorSabor || 90),
    servilletas: Number(initialProductData.config.servilletas || 0),
    cucharas: Number(initialProductData.config.cucharas || 0),
     baseInsumoId: initialProductData.config.baseInsumoId || '',
 baseInsumoName: initialProductData.config.baseInsumoName || '',
  });
  setIceExtraItems(
    (initialProductData.config.extraItems || []).map((item) => {
      const currentProduct = productById.get(item.productId);
      return {
        ...item,
        name: currentProduct?.name || item.name || '',
        unit: getProductBaseUnit(currentProduct) || normalizeRecipeUnit(item.unit),
        qty: Number(item.qty || item.quantity || 0),
      };
    }).filter((item) => item.productId && item.qty > 0)
  );
} else {
  setIceConfig({
    isIceCream: false,
    base: 'cono',
    sabores: 1,
    gramosPorSabor: 90,
    servilletas: 2,
    cucharas: 1
  });
  setIceExtraItems([]);
}
// Precargar receta si existe
if (initialProductData?.config?.recipe) {
  setIsRecipe(true);
  setBaseItems(
    (initialProductData.config.recipe.baseItems || []).map((item) => {
      const currentProduct = productById.get(item.productId);
      const expectedUnit = productUnitById.get(item.productId);
      return {
        ...item,
        name: currentProduct?.name || item.name,
        unit: expectedUnit || normalizeRecipeUnit(item.unit),
      };
    })
  );
  setChoiceGroups(
    (initialProductData.config.recipe.choiceGroups || []).map((group, index) => ({
      id: group.id || `choice-${index + 1}`,
      label: group.label || '',
      min: Number(group.min || 0),
      max: Number(group.max || group.min || 1),
      perPickQty: Number(group.perPickQty || 1),
      unit: normalizeRecipeUnit(group.unit || 'unidad'),
      fromCategory: group.fromCategory || '',
      defaultProductId: group.defaultProductId || '',
    }))
  );
} else {
  setIsRecipe(false);
  setBaseItems([]);
  setChoiceGroups([]);
}

const savedPromotion = initialProductData?.config?.promotion;
const savedPromotionComponents = getPromotionComponents(savedPromotion);
setPromotionConfig(savedPromotion?.enabled ? {
  enabled: true,
  components: savedPromotionComponents.length > 0
    ? savedPromotionComponents
    : [{ sourceProductId: '', sourceProductName: '', quantity: 2 }],
  weekdays: Array.isArray(savedPromotion.weekdays) ? savedPromotion.weekdays.map(Number) : [],
} : {
  enabled: false,
  components: [{ sourceProductId: '', sourceProductName: '', quantity: 2 }],
  weekdays: [],
});

const categoriasSoloTexto = (existingCategories || []).filter(c => typeof c === 'string');

if (categoriaFormateada && !categoriasSoloTexto.includes(categoriaFormateada)) {
  setShowNewCategoryInput(true);
  setNewCategory(categoriaFormateada);
} else {
  setShowNewCategoryInput(false);
  setNewCategory('');
}
  } 
  else {
    // 🆕 Reset para nuevo producto
setProduct({
  name: '',
  barcode: '',
  image: '',
  price: '',
  costPrice: '',
  category: '',
  type: 'final', // 👈
  unit: '',      // 👈
   excludeFromEntries: false,
registeredBy: typeof currentUser === 'string'
  ? currentUser
  : (currentUser?.email
      ?? currentUser?.username
      ?? currentUser?.displayName
      ?? currentUser?.uid
      ?? ''),
  registrationDate: new Date().toISOString().split('T')[0]
});
    setNewCategory('');
    setShowNewCategoryInput(false);
    setIsRecipe(false);
    setBaseItems([]);
    setChoiceGroups([]);
    setPromotionConfig({
      enabled: false,
      components: [{ sourceProductId: '', sourceProductName: '', quantity: 2 }],
      weekdays: [],
    });
  }
}, [initialProductData, currentUser, existingCategories, productUnitById, productById]);

useEffect(() => {
  const s = iceExtraQ.trim().toLowerCase();
  if (s.length < 2) { setIceExtraSuggestions([]); return; }
  if (selectedIceExtraProduct && (selectedIceExtraProduct.name || '').toLowerCase() === s) {
    setIceExtraSuggestions([]);
    return;
  }
  const seen = new Set();
  const list = products
    .filter(p => normalizedType(p) === 'insumo')
    .filter(p =>
      (p.name || '').toLowerCase().includes(s) ||
      (p.barcode || '').toLowerCase().includes(s)
    )
    .filter((prod) => {
      const key = `${prod.name || ''}__${prod.unit || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 20);
  setIceExtraSuggestions(list);
}, [iceExtraQ, products, selectedIceExtraProduct]);

useEffect(() => {
  const s = q.trim().toLowerCase();
  if (s.length < 2) { setSuggestions([]); return; }
  if (selectedBaseProduct && (selectedBaseProduct.name || '').toLowerCase() === s) {
    setSuggestions([]);
    return;
  }
  const seen = new Set();
  const list = products
    .filter(p => normalizedType(p) === 'insumo') // 👈 solo insumos
    .filter(p =>
      (p.name || '').toLowerCase().includes(s) ||
      (p.barcode || '').toLowerCase().includes(s)
    )
    .filter((p) => {
      const key = p.id || `${p.name || ''}::${p.barcode || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 20);
  setSuggestions(list);
}, [q, products, selectedBaseProduct]);

useEffect(() => {
  return () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  };
}, [previewUrl]);


const addBaseItem = (prod) => {
  const target = prod || selectedBaseProduct;
  if (!target?.id) return;
  const lockedUnit = getProductBaseUnit(target);
  setBaseItems(prev => [
    ...prev,
    { productId: target.id, name: target.name, qty: Number(newBaseQty) || 1, unit: lockedUnit }
  ]);
  setQ('');
  setSuggestions([]);
  setNewBaseQty(1);
  setNewBaseUnit('unidad');
  setSelectedBaseProduct(null);
};

const removeBaseItem = (idx) => {
  setBaseItems(prev => prev.filter((_, i) => i !== idx));
};

const addIceExtraItem = (prod) => {
  const target = prod || selectedIceExtraProduct;
  if (!target?.id) return;
  const lockedUnit = getProductBaseUnit(target);
  setIceExtraItems(prev => [
    ...prev,
    { productId: target.id, name: target.name, qty: Number(newIceExtraQty) || 1, unit: lockedUnit }
  ]);
  setIceExtraQ('');
  setIceExtraSuggestions([]);
  setSelectedIceExtraProduct(null);
  setNewIceExtraQty(1);
};

const removeIceExtraItem = (idx) => {
  setIceExtraItems(prev => prev.filter((_, i) => i !== idx));
};

const addChoiceGroup = () => {
  setChoiceGroups(prev => [
    ...prev,
    {
      id: `choice-${Date.now()}-${prev.length + 1}`,
      label: '',
      min: 1,
      max: 1,
      perPickQty: '',
      unit: 'unidad',
      fromCategory: '',
      defaultProductId: '',
    }
  ]);
};

const updateChoiceGroup = (idx, field, value) => {
  setChoiceGroups(prev => prev.map((group, index) => {
    if (index !== idx) return group;
    const next = { ...group, [field]: value };
    if (field === 'pickCount') {
      const count = Math.max(Number(value || 0), 0);
      next.min = count;
      next.max = count;
    }
    if (field === 'perPickQty') {
      next.perPickQty = value;
    }
    if (field === 'unit') {
      next.unit = isIceCreamFlavorCategory(group.fromCategory) ? 'g' : normalizeRecipeUnit(value);
      next.defaultProductId = '';
    }
    if (field === 'fromCategory') {
      next.fromCategory = value;
      next.defaultProductId = '';
      if (isIceCreamFlavorCategory(value)) {
        next.unit = 'g';
      }
    }
    return next;
  }));
};

const removeChoiceGroup = (idx) => {
  setChoiceGroups(prev => prev.filter((_, index) => index !== idx));
};


const handleChange = (e) => {
  const { name, value } = e.target;
  let newValue = value;

 if (name === 'price') {
   newValue = String(value || '').replace(/\D/g, '');
 }

 if (name === 'costPrice') {
   newValue = sanitizeDecimalInput(value);
 }

 if (name === "name") {
   // Forzar todo a MAYÚSCULAS
   newValue = value.toUpperCase();
   }

   if (name === "category") {
    // Capitalizar (primera letra mayúscula, resto minúscula)
    newValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
   }

  setProduct((prev) => ({ ...prev, [name]: newValue }));
 };
const handleImageUpload = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (file.size > 15 * 1024 * 1024) {
  alert('El archivo supera 15MB. Elige una imagen más ligera.');
  return;
}

  try {
    // 1) Preview inmediata
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);

    // 2) Compresión en cliente
    setLoading(true);
    setUploadPct(0);
    const compressed = await compressImageFile(file, { maxDim: MAX_DIM });

    // 3) Subida con progreso real
    const uploadFile = new File([compressed], 'producto.webp', { type: compressed.type || 'image/webp' });
    const res = await uploadImage(uploadFile, 'product', { onProgress: setUploadPct });

    // 4) Guardar URL final en el producto
    setProduct((prev) => ({ ...prev, image: res.secure_url }));
  } catch (err) {
    console.error("❌ Subida de imagen:", err);
    alert("No se pudo subir la imagen. Intenta nuevamente.");
  } finally {
    setLoading(false);
    setTimeout(() => setUploadPct(0), 400); // limpia barra
  }
};

 const handleCategoryChange = (e) => {
 let value = e.target.value;

  if (value === "new-category") {
     setShowNewCategoryInput(true);
     setProduct((prev) => ({ ...prev, category: "" }));
   } else {
    // Capitalizar categoría seleccionada
    value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    setShowNewCategoryInput(false);
    const selectingPromotion = isPromotionCategory(value);
    setProduct((prev) => ({
      ...prev,
      category: value,
      ...(selectingPromotion ? { type: 'final', excludeFromEntries: true } : {}),
    }));
    if (selectingPromotion) {
      setPromotionConfig((prev) => ({ ...prev, enabled: true }));
    }
   }
 };

const onToggleRecipe = (checked) => {
  setIsRecipe(checked);
  if (!checked) {
    setChoiceGroups([]);
  }
  setProduct(prev => ({
    ...prev,
    // si es receta, bloquea ENTRADAS; si deja de serlo, respeta si es helado o lo que ya tuviera
    excludeFromEntries: checked ? true : (!!prev.config?.isIceCream || !!prev.excludeFromEntries),
  }));
};

const onToggleIceCream = (checked) => {
  setIceConfig(prev => ({ ...prev, isIceCream: checked }));
  setProduct(prev => {
    const nextCfg = { ...(prev.config || {}), isIceCream: checked };
    return {
      ...prev,
      config: nextCfg,
      // si es helado => bloquear ENTRADAS; si deja de serlo, respeta si sigue siendo receta
      excludeFromEntries:
        checked ? true :
        (String(prev.type || 'final').toLowerCase() === 'receta' ? true : !!prev.excludeFromEntries),
    };
  });
};



const handleSubmit = (e) => {
  e.preventDefault();
  if (submitting) return; // evita doble clic
  setSubmitting(true);

  // 1) Validar/cerrar categoría nueva
 let finalCategory = product.category;
 if (showNewCategoryInput && newCategory.trim() !== '') {
  const cat = newCategory.trim();
 finalCategory = cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
    
  } else if (showNewCategoryInput && newCategory.trim() === '') {
    alert('Por favor, ingrese el nombre de la nueva categoría.');
    setSubmitting(false);
    return;
  } // 👈 IMPORTANTE: cerramos el if/else aquí

const savingPromotion = isPromotionCategory(finalCategory);
const rawPromotionComponents = Array.isArray(promotionConfig.components) ? promotionConfig.components : [];
const normalizedPromotionComponents = rawPromotionComponents.map((component) => {
  const source = productById.get(String(component?.sourceProductId || '').trim());
  return {
    sourceProductId: source?.id || '',
    sourceProductName: source?.name || '',
    quantity: Number.parseInt(component?.quantity, 10),
    source,
  };
});
const promotionWeekdays = Array.from(new Set(
  (promotionConfig.weekdays || []).map(Number).filter((day) => day >= 0 && day <= 6)
));

if (savingPromotion) {
  if (normalizedPromotionComponents.length === 0 || normalizedPromotionComponents.some(({ source }) => !source?.config?.recipe)) {
    alert('Completa todos los productos con receta incluidos en la promoción.');
    setSubmitting(false);
    return;
  }
  if (normalizedPromotionComponents.length > 10) {
    alert('Una promoción puede incluir máximo 10 productos diferentes.');
    setSubmitting(false);
    return;
  }
  if (normalizedPromotionComponents.some(({ quantity }) => !Number.isInteger(quantity) || quantity < 1 || quantity > 50)) {
    alert('La cantidad de cada producto debe estar entre 1 y 50 unidades.');
    setSubmitting(false);
    return;
  }
  const promotionSourceIds = normalizedPromotionComponents.map(({ sourceProductId }) => sourceProductId);
  if (new Set(promotionSourceIds).size !== promotionSourceIds.length) {
    alert('Cada producto debe aparecer una sola vez. Si es un 2x1, usa cantidad 2 en la misma fila.');
    setSubmitting(false);
    return;
  }
  const totalIncludedUnits = normalizedPromotionComponents.reduce((sum, component) => sum + component.quantity, 0);
  if (totalIncludedUnits > 50) {
    alert('La promoción no puede superar 50 unidades incluidas en total.');
    setSubmitting(false);
    return;
  }
  if (promotionWeekdays.length === 0) {
    alert('Selecciona al menos un día de la semana para mostrar la promoción.');
    setSubmitting(false);
    return;
  }
  if (Number(product.price || 0) <= 0) {
    alert('Ingresa el precio de venta de la promoción.');
    setSubmitting(false);
    return;
  }
}

if (!savingPromotion && iceConfig.isIceCream && !iceConfig.baseInsumoId) {
  alert('Selecciona el insumo base (Vaso/Cono) que se descontará del inventario.');
  setSubmitting(false);
  return;
}

if (!savingPromotion && baseItemsWithUnitMismatch.length > 0) {
  const names = baseItemsWithUnitMismatch.map(item => item.name).join(', ');
  alert(`La unidad de estos ingredientes no coincide con el catálogo: ${names}. Corrige la receta antes de guardar.`);
  setSubmitting(false);
  return;
}

const normalizedChoiceGroups = choiceGroups
  .map((group, index) => ({
    id: String(group.id || `choice-${index + 1}`),
    label: String(group.label || '').trim(),
    min: Number(group.min || 0),
    max: Math.max(Number(group.max || 0), Number(group.min || 0)),
    perPickQty: Number(group.perPickQty || 0),
    unit: normalizeRecipeUnit(group.unit || 'unidad'),
    fromCategory: String(group.fromCategory || '').trim(),
    defaultProductId: String(group.defaultProductId || '').trim(),
  }))
  .filter(group =>
    group.label &&
    group.fromCategory &&
    group.max > 0 &&
    group.perPickQty > 0
  );

if (!savingPromotion && isRecipe && choiceGroups.length > 0 && normalizedChoiceGroups.length !== choiceGroups.length) {
  alert('Revisa las opciones variables de la receta. Cada grupo debe tener nombre, categoría, cantidad y unidad.');
  setSubmitting(false);
  return;
}

if (!savingPromotion && isRecipe && (recipeCostSummary.missingBaseItems.length > 0 || recipeCostSummary.missingChoiceGroups.length > 0)) {
  const missing = [
    ...recipeCostSummary.missingBaseItems,
    ...recipeCostSummary.missingChoiceGroups.map(name => `opción: ${name}`)
  ].join(', ');
  alert(`No pude calcular el costo completo de la receta. Revisa estos insumos/opciones: ${missing}.`);
  setSubmitting(false);
  return;
}

if (!savingPromotion && iceConfig.isIceCream && (iceCreamCostSummary.missingItems.length > 0 || iceCreamCostSummary.missingFlavorCosts)) {
  const missing = [
    ...iceCreamCostSummary.missingItems,
    ...(iceCreamCostSummary.missingFlavorCosts ? ['sabores de helado'] : [])
  ].join(', ');
  alert(`No pude calcular el costo completo del helado. Revisa estos insumos/costos: ${missing}.`);
  setSubmitting(false);
  return;
}

  // 2) Armar config (solo para productos finales con helado/receta)
const configToSave =
 (product.type || 'final') === 'final'
   ? (savingPromotion ? {
       promotion: {
         enabled: true,
         components: normalizedPromotionComponents.map(({ sourceProductId, sourceProductName, quantity }) => ({
           sourceProductId,
           sourceProductName,
           quantity,
         })),
         ...(normalizedPromotionComponents.length === 1 ? {
           sourceProductId: normalizedPromotionComponents[0].sourceProductId,
           sourceProductName: normalizedPromotionComponents[0].sourceProductName,
           quantity: normalizedPromotionComponents[0].quantity,
         } : {}),
         weekdays: promotionWeekdays,
       }
     } : {
       ...(iceConfig.isIceCream ? {
         isIceCream: true,
         base: iceConfig.base,
         baseInsumoId: iceConfig.baseInsumoId || '',
         baseInsumoName: iceConfig.baseInsumoName || '',
         extraItems: iceExtraItems
           .map((item) => ({
             productId: item.productId,
             name: item.name,
             qty: Number(item.qty || 0),
             unit: normalizeRecipeUnit(item.unit || 'unidad'),
           }))
           .filter((item) => item.productId && item.qty > 0),
         sabores: Number(iceConfig.sabores),
         gramosPorSabor: Number(iceConfig.gramosPorSabor),
         servilletas: Number(iceConfig.servilletas || 0),
         cucharas: Number(iceConfig.cucharas || 0),
       } : {}),
       ...(isRecipe ? {
         recipe: { baseItems, choiceGroups: normalizedChoiceGroups }
       } : {})
      })
   : null;

 const safeRegisteredBy =
    typeof product.registeredBy === 'string'
      ? product.registeredBy
      : (product.registeredBy?.email ??
         product.registeredBy?.username ??
         product.registeredBy?.displayName ??
         product.registeredBy?.uid ??
         '');


  // 3) Guardar (UNA sola vez)
  onSave({
    ...product,
    registeredBy: safeRegisteredBy,
    price: (product.type === 'insumo') ? 0 : parseFloat(product.price || 0),
    costPrice: savingPromotion
      ? Number(calculatePromotionComponentsCost(normalizedPromotionComponents, products).toFixed(4))
      : isRecipe
      ? Number(recipeCostSummary.total.toFixed(4))
      : iceConfig.isIceCream
      ? Number(iceCreamCostSummary.total.toFixed(4))
      : Number(product.costPrice || 0),
    category: finalCategory,
 type: String(product.type || 'final').toLowerCase(),
     unit: (product.type === 'insumo') ? (product.unit || 'unidad') : '',
    config: configToSave, // ✅ guardamos la config si aplica
    yieldConfig: product.type === 'insumo' ? {
      enabled: yieldTrackingEnabled,
      expectedYieldPercent: Math.min(
        100,
        Math.max(0, Number(product?.yieldConfig?.expectedYieldPercent ?? 90) || 0)
      ),
    } : null,
    excludeFromEntries: savingPromotion || (product.type !== 'insumo' && !!product.excludeFromEntries),
    id: initialProductData ? initialProductData.id : Date.now(),
  });

  setSubmitting(false);
};

  return (
    <div className="mx-auto w-full max-w-4xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-xl shadow-slate-200/70">
<div className="border-b border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-sky-700 px-4 py-5 sm:px-6">
<p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-200">Productos</p>
<h2 className="mt-2 text-2xl text-center font-black text-white sm:text-left sm:text-3xl">
  {mode === 'edit' ? 'Editar Producto' : mode === 'clone' ? 'Clonar Producto' : 'Registrar Nuevo Producto'}
</h2>
</div>

{/* Aquí mostramos el mensaje mientras sube la imagen */}
<div className="px-4 py-4 sm:px-6 sm:py-6">
{loading && <p className="mb-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-center text-sm font-medium text-sky-700">Subiendo imagen...</p>}

<form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
        <section className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4 sm:p-5">
          <div className="mb-4">
            <h3 className="text-base font-bold text-slate-900">Datos principales</h3>
            <p className="text-sm text-slate-500">Completa la informacion base del producto.</p>
          </div>
        <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Producto</label>
            <input
              type="text"
              name="name"
              value={product.name}
              onChange={handleChange}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código de Barras</label>
            <input
              type="text"
              name="barcode"
              value={product.barcode}
              onChange={handleChange}
               className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            />
          </div>

{/* Precio de Venta (solo requerido si es producto final) */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Precio de Venta</label>
  <input
    type="text"
    name="price"
    value={formatThousandsInput(product.price)}
    onChange={handleChange}
    inputMode="numeric"
    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-400"
    required={(product.type || 'final') !== 'insumo'}
    placeholder={(product.type || 'final') === 'insumo' ? 'No requerido para insumos' : 'Ej: 5000'}
    disabled={(product.type || 'final') === 'insumo'}
  />
</div>

<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Costo actual</label>
  <input
    type="text"
    name="costPrice"
    value={promotionMode
      ? Math.round(promotionCost).toLocaleString('es-CO')
      : formatDecimalThousandsInput(product.costPrice)}
    onChange={handleChange}
    inputMode="decimal"
    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-100"
    placeholder="Ej: 2.500 o 12,5"
    disabled={promotionMode}
  />
  <p className="mt-1 text-xs text-gray-500">
    {promotionMode
      ? 'Se calcula automáticamente con el costo del producto vinculado y la cantidad incluida.'
      : 'Puedes ajustarlo manualmente. La próxima entrada de inventario puede recalcular este costo.'}
  </p>
  {!promotionMode && isRecipe && (
    <div className="mt-2 rounded-2xl border border-blue-100 bg-blue-50 px-3 py-3 text-xs text-blue-800">
      <p>
        Costo estimado de la receta: <strong>${Math.round(recipeCostSummary.total).toLocaleString('es-CO')}</strong>
      </p>
      <p>
        Fijos: ${Math.round(recipeCostSummary.baseCost).toLocaleString('es-CO')}
        {recipeCostSummary.choiceCost > 0 ? ` · Opciones variables estimadas: $${Math.round(recipeCostSummary.choiceCost).toLocaleString('es-CO')}` : ''}
      </p>
      {recipeCostSummary.choiceCost > 0 && (
        <p>
          Las opciones variables se estiman con el costo promedio de su categoría.
        </p>
      )}
      {(recipeCostSummary.missingBaseItems.length > 0 || recipeCostSummary.missingChoiceGroups.length > 0) && (
        <p className="text-red-600">
          Faltan costos o categorías para calcular completo.
        </p>
      )}
      <button
        type="button"
        onClick={() => setProduct(prev => ({ ...prev, costPrice: Number(recipeCostSummary.total.toFixed(4)) }))}
        className="mt-3 w-full rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 sm:w-auto"
      >
        Usar costo calculado
      </button>
    </div>
  )}
  {!promotionMode && !isRecipe && iceConfig.isIceCream && (
    <div className="mt-2 rounded-2xl border border-sky-100 bg-sky-50 px-3 py-3 text-xs text-sky-800">
      <p>
        Costo estimado del helado: <strong>${Math.round(iceCreamCostSummary.total).toLocaleString('es-CO')}</strong>
      </p>
      <p>
        Base: ${Math.round(iceCreamCostSummary.baseCost).toLocaleString('es-CO')}
        {iceCreamCostSummary.toolsCost > 0 ? ` · Accesorios: $${Math.round(iceCreamCostSummary.toolsCost).toLocaleString('es-CO')}` : ''}
        {iceCreamCostSummary.extrasCost > 0 ? ` · Extras fijos: $${Math.round(iceCreamCostSummary.extrasCost).toLocaleString('es-CO')}` : ''}
      </p>
      <p>
        Sabores estimados: ${Math.round(iceCreamCostSummary.flavorCost).toLocaleString('es-CO')}
      </p>
      {iceCreamCostSummary.flavorCost > 0 && (
        <p>Los sabores se calculan con el costo promedio de los insumos de helado en gramos.</p>
      )}
      {iceCreamCostSummary.missingItems.length > 0 && (
        <p className="text-red-600">
          Faltan costos para: {iceCreamCostSummary.missingItems.join(', ')}.
        </p>
      )}
      {iceCreamCostSummary.missingFlavorCosts && (
        <p className="text-red-600">
          No encontrÃ© insumos de sabores de helado en gramos para estimar esa parte del costo.
        </p>
      )}
      <button
        type="button"
        onClick={() => setProduct(prev => ({ ...prev, costPrice: Number(iceCreamCostSummary.total.toFixed(4)) }))}
        className="mt-3 w-full rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 sm:w-auto"
      >
        Usar costo calculado
      </button>
    </div>
  )}
</div>

{/* Tipo de producto */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
  <select
    name="type"
    value={product.type || 'final'}
    onChange={handleChange}
    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
    required
    disabled={promotionMode}
  >
    <option value="final">Producto final (se vende)</option>
    <option value="insumo">Insumo (para recetas)</option>
  </select>
</div>

{(product.type || 'final') !== 'insumo' && !promotionMode && (
<label className="col-span-1 md:col-span-2 flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-gray-700">
  <input
    type="checkbox"
    checked={!!product.excludeFromEntries}
    onChange={(e) =>
      setProduct(prev => ({ ...prev, excludeFromEntries: e.target.checked }))
    }
  />
  <span>
    <span className="block font-semibold">Producto elaborado: permitir venta sin existencias</span>
    <span className="mt-1 block text-xs text-gray-500">
      No recibe entradas. Si no tiene receta, su stock se descuenta y puede quedar negativo; si tiene receta, se descuentan sus insumos.
    </span>
  </span>
</label>
)}



{/* Unidad base (solo si es insumo) */}
{(product.type || 'final') === 'insumo' && (
  <div className="space-y-4">
    <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Unidad base</label>
    <select
      name="unit"
      value={product.unit || 'unidad'}
      onChange={handleChange}
    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      required
    >
      <option value="unidad">Unidad</option>
      <option value="g">Gramos</option>
      <option value="ml">Ml</option>
    </select>
    </div>

    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <label className="flex items-start gap-3 text-sm text-amber-950">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={yieldTrackingEnabled}
          onChange={(e) => setProduct((prev) => ({
            ...prev,
            unit: e.target.checked ? 'g' : prev.unit,
            yieldConfig: {
              enabled: e.target.checked,
              expectedYieldPercent: Number(prev?.yieldConfig?.expectedYieldPercent ?? 90),
            },
          }))}
        />
        <span>
          <span className="block font-semibold">Maneja rendimiento y merma en entradas</span>
          <span className="mt-1 block text-xs text-amber-800">
            Solicita cantidad comprada y cantidad aprovechable; solo la aprovechable aumenta el inventario.
          </span>
        </span>
      </label>

      {yieldTrackingEnabled && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-amber-950">Rendimiento esperado (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={product?.yieldConfig?.expectedYieldPercent ?? 90}
            onChange={(e) => setProduct((prev) => ({
              ...prev,
              yieldConfig: { enabled: true, expectedYieldPercent: e.target.value },
            }))}
            className="mt-1 w-full rounded-xl border border-amber-300 bg-white px-3 py-2"
          />
          <p className="mt-1 text-xs text-amber-800">
            Es informativo; cada entrada utilizará el rendimiento real obtenido.
          </p>
        </div>
      )}
    </div>
  </div>
)}


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
            <select
              name="category"
              value={showNewCategoryInput ? 'new-category' : product.category}
              onChange={handleCategoryChange}
               className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              required
            >
              <option value="">Seleccionar...</option>
{existingCategories
  .filter(c => typeof c === 'string')
  .map(category => (
    <option key={category} value={category}>{category}</option>
))}
              <option value="new-category">Crear nueva categoría...</option>
            </select>
          </div>

          {showNewCategoryInput && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Nueva Categoría</label>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                required
              />
            </div>
          )}

{promotionMode && (
  <div className="col-span-1 md:col-span-2 rounded-3xl border-2 border-fuchsia-200 bg-fuchsia-50 p-4 sm:p-5">
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-fuchsia-700">Configuración obligatoria</p>
      <h4 className="mt-1 text-lg font-bold text-slate-900">Producto promocional</h4>
      <p className="mt-1 text-sm text-slate-600">
        Para un 2x1 usa una fila con cantidad 2. Para un combo, agrega varios productos y asigna la cantidad de cada uno.
      </p>
    </div>

    <div className="mt-4 space-y-3">
      {promotionComponents.map((component, index) => {
        const selectedSource = productById.get(component.sourceProductId);
        const selectedInOtherRow = new Set(
          promotionComponents
            .filter((_, componentIndex) => componentIndex !== index)
            .map((item) => item.sourceProductId)
            .filter(Boolean)
        );
        return (
          <div key={`${component.sourceProductId || 'new'}-${index}`} className="grid grid-cols-1 gap-3 rounded-2xl border border-fuchsia-100 bg-white p-3 md:grid-cols-[minmax(0,1fr)_140px_auto] md:items-end">
            <div>
              <label className="block text-sm font-semibold text-slate-800">Producto con receta</label>
              <select
                value={component.sourceProductId}
                onChange={(event) => {
                  const source = productById.get(event.target.value);
                  setPromotionConfig((prev) => ({
                    ...prev,
                    enabled: true,
                    components: prev.components.map((item, componentIndex) => (
                      componentIndex === index
                        ? { ...item, sourceProductId: event.target.value, sourceProductName: source?.name || '' }
                        : item
                    )),
                  }));
                  setProduct((prev) => ({ ...prev, excludeFromEntries: true, type: 'final' }));
                }}
                className="mt-1 w-full rounded-xl border border-fuchsia-200 bg-white px-3 py-2.5"
                required
              >
                <option value="">Seleccionar producto...</option>
                {promotionSourceProducts.map((source) => (
                  <option key={source.id} value={source.id} disabled={selectedInOtherRow.has(source.id)}>
                    {source.name}
                  </option>
                ))}
              </select>
              {selectedSource && (
                <p className="mt-1 text-xs text-slate-500">
                  Costo por unidad: ${Math.round(Number(selectedSource.costPrice || 0)).toLocaleString('es-CO')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-800">Cantidad</label>
              <input
                type="number"
                min="1"
                max="50"
                step="1"
                value={component.quantity}
                onChange={(event) => setPromotionConfig((prev) => ({
                  ...prev,
                  enabled: true,
                  components: prev.components.map((item, componentIndex) => (
                    componentIndex === index
                      ? { ...item, quantity: Math.max(1, Number.parseInt(event.target.value, 10) || 1) }
                      : item
                  )),
                }))}
                className="mt-1 w-full rounded-xl border border-fuchsia-200 bg-white px-3 py-2.5"
                required
              />
            </div>

            {promotionComponents.length > 1 && (
              <button
                type="button"
                onClick={() => setPromotionConfig((prev) => ({
                  ...prev,
                  components: prev.components.filter((_, componentIndex) => componentIndex !== index),
                }))}
                className="rounded-xl border border-red-200 px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50"
              >
                Quitar
              </button>
            )}
          </div>
        );
      })}

      <button
        type="button"
        disabled={promotionComponents.length >= 10 || promotionSourceProducts.length <= promotionComponents.length}
        onClick={() => setPromotionConfig((prev) => ({
          ...prev,
          enabled: true,
          components: [...prev.components, { sourceProductId: '', sourceProductName: '', quantity: 1 }],
        }))}
        className="rounded-xl border border-dashed border-fuchsia-400 bg-white px-4 py-2.5 text-sm font-semibold text-fuchsia-700 hover:bg-fuchsia-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        + Agregar otro producto al combo
      </button>

      {promotionSourceProducts.length === 0 && (
        <p className="text-xs text-red-600">Primero debes crear al menos un producto con receta.</p>
      )}
    </div>

    <div className="mt-4">
      <p className="text-sm font-semibold text-slate-800">Días disponibles</p>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {PROMOTION_WEEKDAYS.map((day) => {
          const checked = promotionConfig.weekdays.includes(day.value);
          return (
            <label key={day.value} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${checked ? 'border-fuchsia-400 bg-white font-semibold text-fuchsia-800' : 'border-fuchsia-100 bg-fuchsia-50 text-slate-600'}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(event) => setPromotionConfig((prev) => ({
                  ...prev,
                  enabled: true,
                  weekdays: event.target.checked
                    ? Array.from(new Set([...prev.weekdays, day.value]))
                    : prev.weekdays.filter((value) => value !== day.value),
                }))}
              />
              {day.label}
            </label>
          );
        })}
      </div>
    </div>

    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div className="rounded-2xl bg-white p-3">
        <p className="text-xs uppercase text-slate-500">Unidades incluidas</p>
        <p className="mt-1 font-bold text-slate-900">{promotionIncludedUnits}</p>
      </div>
      <div className="rounded-2xl bg-white p-3">
        <p className="text-xs uppercase text-slate-500">Costo total promo</p>
        <p className="mt-1 font-bold text-fuchsia-800">${Math.round(promotionCost).toLocaleString('es-CO')}</p>
      </div>
      <div className="rounded-2xl bg-white p-3">
        <p className="text-xs uppercase text-slate-500">Ganancia estimada</p>
        <p className={`mt-1 font-bold ${Number(product.price || 0) - promotionCost < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
          ${Math.round(Number(product.price || 0) - promotionCost).toLocaleString('es-CO')}
        </p>
      </div>
    </div>
  </div>
)}

{/* --- Configuración para helados (solo productos finales) --- */}
{(product.type || 'final') === 'final' && !promotionMode && (
  <div className="col-span-1 md:col-span-2 mt-2 rounded-3xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-700">Opcional</p>
      <h4 className="mt-1 text-base font-bold text-slate-900">Configuracion para helados</h4>
    </div>
    <label className="inline-flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3">
      <input
        type="checkbox"
        checked={iceConfig.isIceCream}
onChange={(e) => onToggleIceCream(e.target.checked)}
      />
      <span className="font-semibold">Este producto es un helado (sabores variables al vender)</span>
    </label>

    {iceConfig.isIceCream && (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
{/* Tipo de base: define si buscas “CONO” o “VASO” */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Base</label>
  <select
    value={iceConfig.base}
    onChange={(e) => {
      const base = e.target.value;
      setIceConfig(prev => ({
        ...prev,
        base,
        // al cambiar CONO↔VASO, resetea selección del insumo base
        baseInsumoId: '',
        baseInsumoName: ''
      }));
    }}
    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
  >
    <option value="cono">Cono</option>
    <option value="vaso">Vaso</option>
  </select>
</div>

{/* NUEVO: Insumo base exacto a descontar */}
<div className="md:col-span-2">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Insumo base (unidad) a descontar
  </label>
  <select
    value={iceConfig.baseInsumoId}
    onChange={(e) => {
      const id = e.target.value;
      const pick = baseCandidates(products, iceConfig.base).find(x => x.id === id);
      setIceConfig(prev => ({
        ...prev,
        baseInsumoId: id,
        baseInsumoName: pick?.name || ''
      }));
    }}
    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
    required
  >
    <option value="">Seleccionar insumo…</option>
    {baseCandidates(products, iceConfig.base).map(p => (
      <option key={p.id} value={p.id}>{p.name}</option>
    ))}
  </select>

  {baseCandidates(products, iceConfig.base).length === 0 && (
    <p className="mt-1 text-xs text-amber-600">
      No hay insumos “{iceConfig.base}” por unidad. Crea primero, por ejemplo:
      {iceConfig.base === 'vaso'
        ? ' Vaso 8 oz / Vaso 12 oz / Vaso 24 oz'
        : ' Cono azucarado / Cono barquillo'}
    </p>
  )}

  {iceConfig.baseInsumoName && (
    <p className="mt-1 text-xs text-gray-500">
      Seleccionado: {iceConfig.baseInsumoName}
    </p>
  )}
</div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1"># de sabores</label>
          <input
            type="number"
            min="1"
            max="4"
            value={iceConfig.sabores}
            onChange={(e) => setIceConfig(prev => ({ ...prev, sabores: Number(e.target.value || 1) }))}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gramos por sabor</label>
          <input
            type="number"
            min="1"
            value={iceConfig.gramosPorSabor}
            onChange={(e) => setIceConfig(prev => ({ ...prev, gramosPorSabor: Number(e.target.value || 0) }))}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Servilletas por venta</label>
          <input
            type="number"
            min="0"
            value={iceConfig.servilletas}
            onChange={(e) => setIceConfig(prev => ({ ...prev, servilletas: Number(e.target.value || 0) }))}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cucharas por venta</label>
          <input
            type="number"
            min="0"
            value={iceConfig.cucharas}
            onChange={(e) => setIceConfig(prev => ({ ...prev, cucharas: Number(e.target.value || 0) }))}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
          />
        </div>

        <div className="md:col-span-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div>
            <h5 className="font-semibold text-slate-900">Insumos fijos adicionales</h5>
            <p className="text-sm text-slate-500">Se descuentan siempre que se venda este helado.</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
            <div className="md:col-span-2 relative">
              <label className="text-sm">Buscar insumo</label>
              <input
                value={iceExtraQ}
                onChange={e => {
                  setIceExtraQ(e.target.value);
                  setSelectedIceExtraProduct(null);
                }}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                placeholder="Escribe 2+ letras..."
                autoComplete="off"
              />
              {iceExtraSuggestions.length > 0 && (
                <div className="absolute z-10 mt-2 w-full max-h-48 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-xl">
                  {iceExtraSuggestions.map(s => (
                    <div
                      key={s.id}
                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                      onClick={() => {
                        setSelectedIceExtraProduct(s);
                        setIceExtraQ(s.name);
                        setIceExtraSuggestions([]);
                      }}
                    >
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-gray-500">CÃ³digo: {s.barcode || 'â€”'} Â· Unidad: {formatUnitLabel(s.unit)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm">Cantidad</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newIceExtraQty}
                onChange={e => setNewIceExtraQty(e.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
              />
            </div>

            <div>
              <label className="text-sm">Unidad</label>
              <input
                value={selectedIceExtraProduct ? formatUnitLabel(selectedIceExtraProduct.unit) : 'Selecciona un insumo'}
                readOnly
                className="w-full rounded-2xl border border-slate-300 bg-slate-100 px-3 py-3 text-slate-600"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              className="w-full rounded-2xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white hover:bg-sky-700 transition duration-200 md:w-auto"
              disabled={!selectedIceExtraProduct || (Number(newIceExtraQty) || 0) <= 0}
              onClick={() => addIceExtraItem()}
            >
              Agregar insumo fijo
            </button>
          </div>

          {iceExtraItems.length > 0 && (
            <div className="space-y-2">
              {iceExtraItems.map((item, idx) => (
                <div key={`${item.productId}-${idx}`} className="flex items-center justify-between gap-3 rounded-2xl bg-white px-3 py-3">
                  <span>{item.name} â€” {item.qty} {item.unit}</span>
                  <button className="text-red-600" type="button" onClick={() => removeIceExtraItem(idx)}>Quitar</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
)}
{/* === Receta genérica (para cualquier producto final) ======================= */}
{(product.type || 'final') === 'final' && !promotionMode && (
  <div className="col-span-1 md:col-span-2 mt-4 rounded-3xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">Opcional</p>
      <h4 className="mt-1 text-base font-bold text-slate-900">Receta del producto</h4>
    </div>
    <label className="inline-flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3">
      <input
        type="checkbox"
        checked={isRecipe}
onChange={e => onToggleRecipe(e.target.checked)}
      />
      <span className="font-semibold">Este producto es una receta (combina insumos y/o opciones)</span>
    </label>

    {isRecipe && (
      <>
        {/* Ingredientes fijos */}
        <div className="mt-4">
          <h4 className="font-bold mb-2">Ingredientes fijos (siempre se descuentan)</h4>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
            <div className="md:col-span-2 relative">
              <label className="text-sm">Buscar insumo</label>
              <input
                value={q}
                onChange={e => {
                  setQ(e.target.value);
                  setSelectedBaseProduct(null);
                  setNewBaseUnit('unidad');
                }}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                placeholder="Escribe 2+ letras..."
                autoComplete="off"
              />
              {suggestions.length > 0 && (
                <div className="absolute z-10 mt-2 w-full max-h-48 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-xl">
                  {suggestions.map(s => (
                    <div
                      key={s.id}
                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                      onClick={() => {
                        setSelectedBaseProduct(s);
                        setQ(s.name);
                        setNewBaseUnit(getProductBaseUnit(s));
                        setSuggestions([]);
                      }}
                    >
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-gray-500">Código: {s.barcode || '—'} · Unidad: {formatUnitLabel(s.unit)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm">Cantidad</label>
              <input
                type="number"
                min="0"
                value={newBaseQty}
                onChange={e => setNewBaseQty(e.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
              />
            </div>

            <div>
              <label className="text-sm">Unidad</label>
              <select
                value={newBaseUnit}
                onChange={e => setNewBaseUnit(e.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                disabled={!!selectedBaseProduct}
              >
                <option value="unidad">unidad</option>
                <option value="g">g</option>
                <option value="ml">ml</option>
                       </select>
              {selectedBaseProduct && (
                <p className="mt-1 text-xs text-amber-700">
                  Unidad bloqueada por el insumo: {formatUnitLabel(selectedBaseProduct.unit)}.
                </p>
              )}
                     </div>
                   </div>
                   <div className="mt-3 flex justify-end">
                     <button
                       type="button"
                       className="w-full rounded-2xl bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 transition duration-200 md:w-auto"
                       disabled={!selectedBaseProduct || (Number(newBaseQty) || 0) <= 0}
                       onClick={() => addBaseItem()}
                     >
                       Agregar ingrediente
                     </button>
                   </div>

            {baseItems.length > 0 && (
            <div className="mt-3 space-y-2">
              {baseItems.map((bi, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-3">
                  <span>{bi.name} — {bi.qty} {bi.unit}</span>
                  <button className="text-red-600" type="button" onClick={() => removeBaseItem(idx)}>Quitar</button>
                </div>
              ))}
            </div>
          )}

          {baseItemsWithUnitMismatch.length > 0 && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              Hay ingredientes con unidad distinta a la del catálogo. Debes quitarlos y agregarlos de nuevo antes de guardar.
            </div>
          )}

          <div className="mt-5 border-t pt-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h5 className="font-bold text-gray-800">Sabores u opciones que el cliente elige al vender</h5>
                <p className="text-xs text-gray-500">
                  Define cuántos sabores puede escoger el cliente y cuánto inventario se descuenta por cada uno.
                </p>
              </div>
              <button
                type="button"
                onClick={addChoiceGroup}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Agregar opción
              </button>
            </div>

            {choiceGroups.length === 0 && (
              <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500">
                Esta receta no tiene opciones variables. Si la ensalada debe pedir sabor de helado, agrégalo aquí.
              </div>
            )}

            <div className="mt-3 space-y-3">
              {choiceGroups.map((group, idx) => (
                <div key={group.id || idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm">Nombre de la opción</label>
                      <input
                        type="text"
                        value={group.label || ''}
                        onChange={e => updateChoiceGroup(idx, 'label', e.target.value)}
                        className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                        placeholder="Ej: Sabor de helado"
                      />
                    </div>

                    <div>
                      <label className="text-sm">Categoría de insumos</label>
                      <select
                        value={group.fromCategory || ''}
                        onChange={e => updateChoiceGroup(idx, 'fromCategory', e.target.value)}
                        className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                      >
                        <option value="">Seleccionar categoría...</option>
                        {insumoCategories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="text-sm">Cantidad de sabores</label>
                      <input
                        type="number"
                        min="0"
                        value={group.max ?? group.min ?? 0}
                        onChange={e => updateChoiceGroup(idx, 'pickCount', e.target.value)}
                        className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                      />
                    </div>
                    <div>
                      <label className="text-sm">Cantidad por sabor</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={group.perPickQty ?? 1}
                        onChange={e => updateChoiceGroup(idx, 'perPickQty', e.target.value)}
                        className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                      />
                    </div>
                    <div>
                      <label className="text-sm">Unidad de descuento</label>
                      <select
                        value={group.unit || 'unidad'}
                        onChange={e => updateChoiceGroup(idx, 'unit', e.target.value)}
                        disabled={isIceCreamFlavorCategory(group.fromCategory)}
                        className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                      >
                        <option value="unidad">unidad</option>
                        <option value="g">g</option>
                        <option value="ml">ml</option>
                      </select>
                      {isIceCreamFlavorCategory(group.fromCategory) && (
                        <p className="mt-1 text-xs text-amber-700">
                          Para sabores de helado la unidad se descuenta siempre en gramos.
                        </p>
                      )}
                    </div>
                  </div>

                  {group.fromCategory && (
                    <div>
                      <label className="text-sm">Opción seleccionada por defecto</label>
                      <select
                        value={group.defaultProductId || ''}
                        onChange={e => updateChoiceGroup(idx, 'defaultProductId', e.target.value)}
                        className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3"
                      >
                        <option value="">Sin selección predeterminada</option>
                        {(products || [])
                          .filter(prod => (
                            normalizedType(prod) === 'insumo'
                            && prod.deleted !== true
                            && norm(prod.category || '') === norm(group.fromCategory || '')
                            && normalizeRecipeUnit(prod.unit) === normalizeRecipeUnit(group.unit || 'unidad')
                          ))
                          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' }))
                          .map(prod => (
                            <option key={prod.id} value={prod.id}>{prod.name}</option>
                          ))}
                      </select>
                      <p className="mt-1 text-xs text-slate-500">
                        En POS y Toma de Pedidos aparecerá elegida, pero el usuario podrá cambiarla.
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeChoiceGroup(idx)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Quitar opción
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </>
    )}
  </div>
)}


<div className="col-span-1 md:col-span-2 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Imagen del producto
  </label>
  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
    <input
      type="file"
      accept="image/*"
      onChange={handleImageUpload}
      className="w-full text-sm text-slate-600 file:mr-3 file:rounded-2xl file:border-0 file:bg-slate-900 file:px-4 file:py-3 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800"
    />
    {(product.image || previewUrl) && (
      <img
        src={product.image || previewUrl}
        alt="preview"
        className="h-20 w-20 rounded-2xl object-cover shadow"
        loading="lazy"
      />
    )}
  </div>

  {/* Progreso */}
  {uploadPct > 0 && (
    <div className="mt-3">
      <div className="h-2 w-full bg-gray-200 rounded">
        <div
          className="h-2 bg-blue-600 rounded transition-all"
          style={{ width: `${uploadPct}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">Subiendo {uploadPct}%…</p>
    </div>
  )}
</div>
        </div>
        </section>

        <div className="sticky bottom-0 z-10 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="w-full rounded-2xl bg-slate-200 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-300 transition duration-200 sm:w-auto"
          >
            Cancelar
          </button>
<button
  type="submit"
  disabled={submitting}
  className="w-full rounded-2xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 disabled:opacity-60 sm:w-auto"
>
  {submitting ? 'Guardando...' : (mode === 'edit' ? 'Guardar Cambios' : mode === 'clone' ? 'Crear Clon' : 'Guardar Producto')}
</button>
          </div>
        </div>
      </form>
      </div>
    </div>
  );
};

export default ProductForm;
